import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Box, CircularProgress } from '@mui/material';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Queue from './pages/Queue';
import ThreadDetail from './pages/ThreadDetail';
import Users from './pages/Users';
import CreateCheckin from './pages/CreateCheckin';
import UserTimeline from './pages/UserTimeline';
import Settings from './pages/Settings';

// Layout
import Layout from './components/Layout';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, loggingIn } = useAuth();

  // Show loading during initial auth check OR during login transition
  if (loading || loggingIn) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated, loading, loggingIn, user } = useAuth();

  // Show loading during initial auth check or login transition
  if (loading || loggingIn) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public route */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />

      {/* Protected routes - key by user ID to force remount on account switch */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout key={user?.id || 'logged-out'} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard key={user?.id} />} />
        <Route path="queue" element={<Queue key={user?.id} />} />
        <Route path="thread/:referenceId" element={<ThreadDetail />} />
        <Route path="users" element={<Users key={user?.id} />} />
        <Route path="users/:userId/create-checkin" element={<CreateCheckin />} />
        <Route path="users/:userId/timeline" element={<UserTimeline />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
